import { useEffect, useRef } from 'react';
import { ICON_COLOR } from '@zep-us/zus';
import { PlusSmallIcon } from '@zep-us/zus-icons';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { overlay } from 'overlay-kit';
import { useLocale } from '@/hooks';
import { PromotionModal } from '@/shared/components/PromotionModal';
import { CLASSROOM_ROUTES } from '@/shared/constants/routes';
import { openClassroomTab, trackClassroomEvent } from '@/shared/features/classroom/utils';
const noop = () => undefined;
const SUPPORTED_NEW_CLASS_IMAGE_LOCALES = ['en', 'id', 'ja', 'ko', 'th'] as const;

// ko / en / ja 가이드만 존재. 그 외 locale은 en으로 fallback.
const SUPPORTED_GUIDE_LOCALES = ['ko', 'en', 'ja'] as const;
interface ClassroomPromotionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDismiss: () => void;
  /** 모달이 노출된 맥락(예: public_auto_promotion). 자동 노출 시 출처 추적용. */
  triggeredOn?: string;
}
export const ClassroomPromotionModal = ({
  isOpen,
  onClose,
  onDismiss,
  triggeredOn
}: ClassroomPromotionModalProps) => {
  const router = useRouter();
  const {
    t
  } = useTranslation(['common']);
  const {
    locale
  } = useLocale();
  const hasTrackedView = useRef(false);
  const language = locale.split('-')[0];
  const imageLocale = SUPPORTED_NEW_CLASS_IMAGE_LOCALES.includes(language as (typeof SUPPORTED_NEW_CLASS_IMAGE_LOCALES)[number]) ? language : 'en';
  const guideLocale = SUPPORTED_GUIDE_LOCALES.includes(language as (typeof SUPPORTED_GUIDE_LOCALES)[number]) ? language : 'en';
  const classroomGuideLink = `https://docs.channel.io/zepquiz/${guideLocale}/categories/Creating-a-Class-9629fc5f`;
  useEffect(() => {
    if (isOpen && !hasTrackedView.current) {
      trackClassroomEvent('view_classroom_promotion_modal', {
        source: 'classroom_promotion_modal',
        triggeredOn
      });
      hasTrackedView.current = true;
    }
  }, [isOpen, triggeredOn]);
  const handlePrimaryClick = () => {
    trackClassroomEvent('click_classroom_promotion_create', {
      source: 'classroom_promotion_modal',
      triggeredOn
    });
    onDismiss();
    onClose();
    void router.push(CLASSROOM_ROUTES.list());
  };
  const handleSecondaryClick = () => {
    trackClassroomEvent('click_classroom_promotion_learn_more', {
      source: 'classroom_promotion_modal',
      triggeredOn
    });
    openClassroomTab(classroomGuideLink);
  };
  return <PromotionModal isOpen={isOpen} onClose={() => {
    onDismiss();
    onClose();
  }} title={t('common:classroomPromotion.title')} description={t('common:classroomPromotion.description')} imageUrl={`/assets/new_class_${imageLocale}.webp`} badge={{
    label: 'New',
    className: 'bg-blue-50 border-opacity-assistive',
    textClassName: 'text-blue-500'
  }} primaryLabel={t('common:classroomPromotion.primaryButton')} primaryRightIcon={<PlusSmallIcon size="xs" fill={ICON_COLOR.inverse} />} primaryClassName="bg-gradient-to-r from-blue-400 to-mint-600" onPrimaryClick={handlePrimaryClick} secondaryLabel={t('common:classroomPromotion.secondaryButton')} onSecondaryClick={handleSecondaryClick} data-sentry-element="PromotionModal" data-sentry-component="ClassroomPromotionModal" data-sentry-source-file="ClassroomPromotionModal.tsx" />;
};
export function showClassroomPromotionModal({
  triggeredOn
}: {
  triggeredOn?: string;
} = {}) {
  overlay.open(({
    close,
    isOpen
  }) => <ClassroomPromotionModal isOpen={isOpen} onClose={close} onDismiss={noop} triggeredOn={triggeredOn} />);
}